import { useRouter } from "@tanstack/react-router";

/**
 * OAuth 콜백 에러/이상 진입 시 표시하는 공용 에러 화면.
 *
 * 시나리오:
 *  - 사용자가 동의 화면에서 취소 (error=access_denied)
 *  - 토큰 교환 실패 (네트워크/CORS/서버 일시 장애)
 *  - code/state 누락 (비정상 진입)
 *  - OAuth 콘솔 설정 누락 (redirect_uri_mismatch 등)
 *
 * UX:
 *  - 친근한 에러 메시지 + 원인 코드(작게)
 *  - [다시 시도] : 같은 SNS 로그인 재실행 (일시 장애 회복용)
 *  - [로그인 화면으로] : / 로 이동
 */

type Provider = "kakao" | "google" | "naver" | "apple";

interface OAuthErrorViewProps {
  /** OAuth provider 가 반환한 error 코드 또는 우리가 정한 코드 */
  error: string;
  /** 어떤 SNS 의 콜백인지 — 다시 시도 라벨에 사용 */
  provider: Provider;
  /** 다시 시도 시 OAuth URL — 없으면 / 로 보냄 */
  retryUrl?: string;
}

const PROVIDER_LABEL: Record<Provider, string> = {
  kakao: "카카오",
  google: "구글",
  naver: "네이버",
  apple: "Apple",
};

const ERROR_MESSAGE: Record<string, string> = {
  // OAuth 표준 에러
  access_denied: "로그인이 취소되었어요.",
  invalid_request: "잘못된 요청이에요. 다시 시도해 주세요.",
  invalid_client: "클라이언트 인증 정보에 문제가 있어요.",
  invalid_grant: "인증 정보가 만료되었어요. 다시 시도해 주세요.",
  unauthorized_client: "접근이 허용되지 않은 클라이언트예요.",
  unsupported_response_type: "지원되지 않는 요청 방식이에요.",
  invalid_scope: "요청 권한이 잘못되었어요.",
  server_error: "일시적인 서버 오류가 발생했어요.",
  temporarily_unavailable: "잠시 후 다시 시도해 주세요.",
  // 우리 코드 자체
  no_code: "로그인 정보를 받지 못했어요.",
  token_exchange_failed: "로그인 처리 중 문제가 발생했어요.",
  network_error: "네트워크 연결이 불안정해요. 잠시 후 다시 시도해 주세요.",
};

export const OAuthErrorView = ({
  error,
  provider,
  retryUrl,
}: OAuthErrorViewProps) => {
  const { history } = useRouter();

  const message =
    ERROR_MESSAGE[error] ||
    `${PROVIDER_LABEL[provider]} 로그인 중 문제가 발생했어요.`;

  const goLogin = () => history.push("/");

  const retry = () => {
    if (retryUrl) {
      window.location.href = retryUrl;
    } else {
      history.push("/");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFF4E6]">
      <div className="flex-auto flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl border border-[#FFE9D1] w-full max-w-sm p-8 text-center">
          <div className="text-5xl mb-4">😢</div>
          <div className="text-lg font-bold text-[#111] mb-2">
            로그인하지 못했어요
          </div>
          <div className="text-sm text-[#666] leading-relaxed mb-6 whitespace-pre-line">
            {message}
          </div>

          <div className="space-y-2">
            <button
              type="button"
              onClick={retry}
              className="w-full py-3 rounded-full bg-orange-400 hover:bg-orange-500 text-white font-semibold transition-colors"
            >
              다시 시도하기
            </button>
            <button
              type="button"
              onClick={goLogin}
              className="w-full py-3 rounded-full border border-[#E5E5E5] text-[#666] font-semibold hover:bg-[#FAFAFA] transition-colors"
            >
              로그인 화면으로
            </button>
          </div>

          <div className="mt-6 text-[10px] text-[#999]">
            {PROVIDER_LABEL[provider]} · {error}
          </div>
        </div>
      </div>
    </div>
  );
};
